import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Badge } from "@/components/ui/badge"
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table"
import { TabGroup, TabGroupList, TabGroupTrigger, TabGroupContent } from "@/components/ui/tab-group"
import { getApiUrl } from "@/config/api.config"

interface ResponseField {
  key: string
  type: string
  description: string
  example: string
}

interface ApiEndpointSection {
  method: "GET" | "POST"
  urls: string[]
  fields: ResponseField[]
}

export default function ApiResponsePage() {
  const apiEndpoints: ApiEndpointSection[] = [
    {
      method: "POST",
      urls: [
        getApiUrl("/api/v3/verify/qr-code"),
        getApiUrl("/api/v3/verify/image"),
        getApiUrl("/api/v3/verify/base64"),
        getApiUrl("/api/v3/verify/url"),
      ],
      fields: [
        { key: "code", type: "String", description: "รหัสสถานะ Response", example: '"200000"' },
        { key: "message", type: "String", description: "ข้อความอธิบายสถานะ", example: '"Verification completed successfully"' },
        { key: "data", type: "Object | undefined", description: "ผลลัพธ์ข้อมูลทั้งหมด (มีเฉพาะเมื่อสำเร็จ)", example: "{ ... }" },
        { key: "data.referenceId", type: "String", description: "รหัสอ้างอิงการตรวจสอบในระบบ", example: '"ver_1717997820509_abc123"' },
        { key: "data.decode", type: "String", description: "ข้อมูล QR Code Raw", example: '"00020101021129..."' },
        { key: "data.transRef", type: "String | null", description: "รหัสอ้างอิงจากธนาคารผู้โอน", example: '"015073144014FAT0099Y"' },
        { key: "data.dateTime", type: "String", description: "วันและเวลาที่โอน (ISO 8601)", example: '"2024-05-29T05:37:00.000Z"' },
        { key: "data.amount", type: "Number", description: "จำนวนเงินที่โอน", example: "100.00" },
        { key: "data.ref1", type: "String | null", description: "รหัสอ้างอิง 1", example: '"INV001" | null' },
        { key: "data.ref2", type: "String | null", description: "รหัสอ้างอิง 2", example: '"ORDER123" | null' },
        { key: "data.ref3", type: "String | null", description: "รหัสอ้างอิง 3", example: "null" },
        { key: "data.receiver", type: "Object", description: "ข้อมูลผู้รับเงิน", example: "{ ... }" },
        { key: "data.receiver.account", type: "Object", description: "ข้อมูลบัญชีผู้รับ", example: "{ ... }" },
        { key: "data.receiver.account.name", type: "String | null", description: "ชื่อบัญชีผู้รับ", example: '"บริษัท สลิปเช็ค จำกัด"' },
        { key: "data.receiver.account.bank", type: "Object", description: "ข้อมูลธนาคารบัญชีผู้รับ", example: "{ ... }" },
        { key: "data.receiver.account.bank.account", type: "String | null", description: "เลขที่บัญชีผู้รับ", example: '"xxxx-x-5366-x"' },
        { key: "data.receiver.account.proxy", type: "Object | null", description: "ข้อมูล Proxy ผู้รับ (PromptPay)", example: "{ ... } | null" },
        { key: "data.receiver.account.proxy.type", type: "String | null", description: "ประเภท Proxy: NATID, MSISDN, EWALLETID, BILLERID", example: '"NATID"' },
        { key: "data.receiver.account.proxy.account", type: "String | null", description: "เลข Proxy ผู้รับ", example: '"xxx-x-x5366-x"' },
        { key: "data.receiver.bank", type: "Object", description: "ข้อมูลธนาคารผู้รับ", example: "{ ... }" },
        { key: "data.receiver.bank.id", type: "String | null", description: "รหัสธนาคารผู้รับ (3 หลัก)", example: '"004"' },
        { key: "data.receiver.bank.name", type: "String | null", description: "ชื่อธนาคารผู้รับ", example: '"ธนาคารกสิกรไทย"' },
        { key: "data.sender", type: "Object", description: "ข้อมูลผู้ส่งเงิน", example: "{ ... }" },
        { key: "data.sender.account", type: "Object", description: "ข้อมูลบัญชีผู้ส่ง", example: "{ ... }" },
        { key: "data.sender.account.name", type: "String | null", description: "ชื่อบัญชีผู้ส่ง", example: '"นาย สมชาย ทดสอบ"' },
        { key: "data.sender.account.bank", type: "Object", description: "ข้อมูลธนาคารบัญชีผู้ส่ง", example: "{ ... }" },
        { key: "data.sender.account.bank.account", type: "String | null", description: "เลขที่บัญชีผู้ส่ง", example: '"xxx-x-9866-x"' },
        { key: "data.sender.bank", type: "Object", description: "ข้อมูลธนาคารผู้ส่ง", example: "{ ... }" },
        { key: "data.sender.bank.id", type: "String | null", description: "รหัสธนาคารผู้ส่ง (3 หลัก)", example: '"004"' },
        { key: "data.sender.bank.name", type: "String | null", description: "ชื่อธนาคารผู้ส่ง", example: '"ธนาคารกสิกรไทย"' },
        { key: "data.conditionResults", type: "Object | undefined", description: "ผลการตรวจสอบเงื่อนไข (มีเฉพาะเมื่อส่ง checkCondition และผ่านทั้งหมด)", example: "{ ... }" },
        { key: "data.conditionResults.duplicate", type: "Object | undefined", description: "ผลตรวจสอบสลิปซ้ำ", example: "{ ... }" },
        { key: "data.conditionResults.duplicate.passed", type: "Boolean", description: "ผ่านการตรวจสอบหรือไม่", example: "true" },
        { key: "data.conditionResults.duplicate.reason", type: "String", description: "เหตุผล", example: '"No duplicate found"' },
        { key: "data.conditionResults.receiver", type: "Object | undefined", description: "ผลตรวจสอบผู้รับ", example: "{ ... }" },
        { key: "data.conditionResults.receiver.passed", type: "Boolean", description: "ผ่านการตรวจสอบหรือไม่", example: "true" },
        { key: "data.conditionResults.receiver.matchedConditionIndex", type: "Number | undefined", description: "index ของเงื่อนไขที่ตรงกัน", example: "0" },
        { key: "data.conditionResults.amount", type: "Object | undefined", description: "ผลตรวจสอบจำนวนเงิน", example: "{ ... }" },
        { key: "data.conditionResults.amount.passed", type: "Boolean", description: "ผ่านการตรวจสอบหรือไม่", example: "true" },
        { key: "data.conditionResults.amount.expectedAmount", type: "Number", description: "จำนวนเงินที่คาดหวัง", example: "100" },
        { key: "data.conditionResults.amount.actualAmount", type: "Number", description: "จำนวนเงินจริง", example: "100" },
        { key: "data.conditionResults.date", type: "Object | undefined", description: "ผลตรวจสอบวันที่", example: "{ ... }" },
        { key: "data.conditionResults.date.passed", type: "Boolean", description: "ผ่านการตรวจสอบหรือไม่", example: "true" },
        { key: "data.conditionResults.date.expectedDate", type: "String", description: "วันที่คาดหวัง (ISO 8601)", example: '"2025-10-05T14:48:00.000Z"' },
        { key: "data.conditionResults.date.actualDate", type: "String", description: "วันที่จริง (ISO 8601)", example: '"2025-10-05T05:37:00.000Z"' },
      ],
    },
    {
      method: "POST",
      urls: [
        getApiUrl("/api/v3/generate/promptpay"),
      ],
      fields: [
        { key: "success", type: "Boolean", description: "สถานะการทำงานสำเร็จ", example: "true" },
        { key: "code", type: "String", description: "รหัสสถานะ Response", example: '"200100"' },
        { key: "type", type: "String", description: "ประเภทสถานะ", example: '"QR_GENERATED"' },
        { key: "message", type: "String", description: "ข้อความอธิบายสถานะ", example: '"QR code generated successfully"' },
        { key: "data", type: "Object", description: "ข้อมูล QR Code", example: "{ ... }" },
        { key: "data.qrId", type: "String", description: "รหัสอ้างอิง QR Code ในระบบ", example: '"qr_1717997820509_abc123"' },
        { key: "data.qrType", type: "String", description: "ประเภท QR Code", example: '"promptpay"' },
        { key: "data.format", type: "String", description: "รูปแบบ Output: png, svg, dataurl", example: '"png"' },
        { key: "data.qrData", type: "String", description: "ข้อมูล QR Code (base64 PNG / SVG string / data URL)", example: '"iVBORw0KGgoAAAANSUhEUg..."' },
        { key: "data.payload", type: "String", description: "ข้อมูล EMVCo Payload", example: '"00020101021129370016A000000677010112..."' },
        { key: "data.accountInfo", type: "Object", description: "ข้อมูลบัญชีที่ใช้สร้าง QR", example: "{ ... }" },
        { key: "data.accountInfo.accountType", type: "String", description: "ประเภทบัญชี", example: '"PHONE"' },
        { key: "data.accountInfo.accountId", type: "String", description: "เลขบัญชี (masked)", example: '"0612***"' },
        { key: "data.amount", type: "Number | null", description: "จำนวนเงิน (null = ไม่ระบุ)", example: "1000.00" },
        { key: "data.timestamp", type: "String", description: "วันและเวลาที่สร้าง QR Code", example: '"2024-05-29T05:37:00.000Z"' },
        { key: "data.credits", type: "Object", description: "ข้อมูลเครดิตที่หัก", example: "{ ... }" },
        { key: "data.credits.used", type: "Number", description: "เครดิตที่ใช้ไป", example: "1" },
        { key: "meta", type: "Object", description: "ข้อมูล Metadata ของ Response", example: "{ ... }" },
        { key: "meta.requestId", type: "String", description: "รหัส Request ID", example: '"req_1717997820509_abc123"' },
        { key: "meta.timestamp", type: "String", description: "เวลา Response (ISO 8601)", example: '"2024-05-29T05:37:00.000Z"' },
        { key: "meta.processingTime", type: "Number", description: "เวลาในการประมวลผล Request (มิลลิวินาที)", example: "50" },
      ],
    },
    {
      method: "POST",
      urls: [getApiUrl("/api/v3/generate/bill-payment")],
      fields: [
        { key: "success", type: "Boolean", description: "สถานะการทำงานสำเร็จ", example: "true" },
        { key: "code", type: "String", description: "รหัสสถานะ Response", example: '"200100"' },
        { key: "type", type: "String", description: "ประเภทสถานะ", example: '"QR_GENERATED"' },
        { key: "message", type: "String", description: "ข้อความอธิบายสถานะ", example: '"QR code generated successfully"' },
        { key: "data", type: "Object", description: "ข้อมูล QR Code", example: "{ ... }" },
        { key: "data.qrId", type: "String", description: "รหัสอ้างอิง QR Code ในระบบ", example: '"qr_1717997820509_abc123"' },
        { key: "data.qrType", type: "String", description: "ประเภท QR Code", example: '"bill-payment"' },
        { key: "data.format", type: "String", description: "รูปแบบ Output: png, svg, dataurl", example: '"png"' },
        { key: "data.qrData", type: "String", description: "ข้อมูล QR Code (base64 PNG / SVG string / data URL)", example: '"iVBORw0KGgoAAAANSUhEUg..."' },
        { key: "data.payload", type: "String", description: "ข้อมูล EMVCo Payload", example: '"00020101021129370016A000000677010112..."' },
        { key: "data.amount", type: "Number | null", description: "จำนวนเงิน (null = ไม่ระบุ)", example: "5000.00" },
        { key: "data.timestamp", type: "String", description: "วันและเวลาที่สร้าง QR Code", example: '"2024-05-29T05:37:00.000Z"' },
        { key: "data.credits", type: "Object", description: "ข้อมูลเครดิตที่หัก", example: "{ ... }" },
        { key: "data.credits.used", type: "Number", description: "เครดิตที่ใช้ไป", example: "1" },
        { key: "meta", type: "Object", description: "ข้อมูล Metadata ของ Response", example: "{ ... }" },
        { key: "meta.requestId", type: "String", description: "รหัส Request ID", example: '"req_1717997820509_abc123"' },
        { key: "meta.timestamp", type: "String", description: "เวลา Response (ISO 8601)", example: '"2024-05-29T05:37:00.000Z"' },
        { key: "meta.processingTime", type: "Number", description: "เวลาในการประมวลผล Request (มิลลิวินาที)", example: "45" },
      ],
    },
    {
      method: "GET",
      urls: [getApiUrl("/api/v3/history/verifications")],
      fields: [
        { key: "success", type: "Boolean", description: "สถานะการทำงานสำเร็จ", example: "true" },
        { key: "code", type: "String", description: "รหัสสถานะ Response", example: '"200200"' },
        { key: "type", type: "String", description: "ประเภทสถานะ", example: '"HISTORY_RETRIEVED"' },
        { key: "message", type: "String", description: "ข้อความอธิบายสถานะ", example: '"History retrieved successfully"' },
        { key: "data", type: "Object", description: "ข้อมูลประวัติ", example: "{ ... }" },
        { key: "data.history", type: "Array", description: "รายการประวัติการใช้งาน (verify + generate)", example: "[{ ... }]" },
        { key: "data.history[].id", type: "String", description: "รหัสรายการ (verification_id หรือ generation_id)", example: '"ver_1717997820509_abc123"' },
        { key: "data.history[].type", type: "String", description: "ประเภทรายการ: verification, generation", example: '"verification"' },
        { key: "data.history[].status", type: "String", description: "สถานะ: success, failed", example: '"success"' },
        { key: "data.history[].amount", type: "Number | null", description: "จำนวนเงิน", example: "100.00" },
        { key: "data.history[].credits", type: "Number", description: "เครดิตที่ใช้ในรายการนี้", example: "1" },
        { key: "data.history[].createdAt", type: "String", description: "วันที่ทำรายการ", example: '"2025-02-15T10:30:00.000Z"' },
        { key: "data.pagination", type: "Object", description: "ข้อมูล Pagination", example: "{ ... }" },
        { key: "data.pagination.currentPage", type: "Number", description: "หน้าปัจจุบัน", example: "1" },
        { key: "data.pagination.totalPages", type: "Number", description: "จำนวนหน้าทั้งหมด", example: "5" },
        { key: "data.pagination.totalItems", type: "Number", description: "จำนวนรายการทั้งหมด", example: "100" },
        { key: "data.pagination.itemsPerPage", type: "Number", description: "จำนวนรายการต่อหน้า", example: "20" },
        { key: "data.pagination.hasNextPage", type: "Boolean", description: "มีหน้าถัดไปหรือไม่", example: "true" },
        { key: "data.pagination.hasPreviousPage", type: "Boolean", description: "มีหน้าก่อนหน้าหรือไม่", example: "false" },
        { key: "data.statistics", type: "Object", description: "ข้อมูลสถิติสรุป", example: "{ ... }" },
        { key: "data.statistics.totalVerifications", type: "Number", description: "จำนวนการตรวจสอบทั้งหมด", example: "80" },
        { key: "data.statistics.totalGenerations", type: "Number", description: "จำนวนการสร้าง QR ทั้งหมด", example: "20" },
        { key: "data.statistics.successRate", type: "String", description: "อัตราความสำเร็จ (%)", example: '"95.00"' },
        { key: "data.statistics.totalCreditsUsed", type: "Number", description: "เครดิตที่ใช้ทั้งหมด", example: "100" },
        { key: "meta", type: "Object", description: "ข้อมูล Metadata ของ Response", example: "{ ... }" },
        { key: "meta.requestId", type: "String", description: "รหัส Request ID", example: '"req_1717997820509_abc123"' },
        { key: "meta.timestamp", type: "String", description: "เวลา Response (ISO 8601)", example: '"2024-05-29T05:37:00.000Z"' },
        { key: "meta.processingTime", type: "Number", description: "เวลาในการประมวลผล Request (มิลลิวินาที)", example: "15" },
      ],
    },
    {
      method: "GET",
      urls: [getApiUrl("/api/v3/account/info")],
      fields: [
        { key: "success", type: "Boolean", description: "สถานะการทำงานสำเร็จ", example: "true" },
        { key: "code", type: "String", description: "รหัสสถานะ Response", example: '"200300"' },
        { key: "type", type: "String", description: "ประเภทสถานะ", example: '"ACCOUNT_INFO_RETRIEVED"' },
        { key: "message", type: "String", description: "ข้อความอธิบายสถานะ", example: '"Account information retrieved successfully"' },
        { key: "data", type: "Object", description: "ข้อมูลบัญชี", example: "{ ... }" },
        { key: "data.account", type: "Object", description: "รายละเอียดบัญชี", example: "{ ... }" },
        { key: "data.account.customerId", type: "String", description: "รหัสลูกค้า", example: '"cust-001"' },
        { key: "data.account.businessName", type: "String", description: "ชื่อธุรกิจ", example: '"สวัสดี เซลส์ เอนเตอร์"' },
        { key: "data.account.email", type: "String", description: "อีเมล", example: '"user@example.com"' },
        { key: "data.account.phone", type: "String", description: "เบอร์โทรศัพท์", example: '"0812345678"' },
        { key: "data.account.package", type: "Object", description: "ข้อมูลแพ็คเกจ", example: "{ ... }" },
        { key: "data.account.package.name", type: "String", description: "ชื่อแพ็คเกจ", example: '"Pro"' },
        { key: "data.account.package.tier", type: "String", description: "ระดับแพ็คเกจ", example: '"pro"' },
        { key: "data.account.package.creditsRemaining", type: "Number", description: "เครดิตคงเหลือ", example: "5000" },
        { key: "data.account.package.creditsTotal", type: "Number", description: "เครดิตทั้งหมดในแพ็คเกจ", example: "10000" },
        { key: "data.account.package.creditsUsed", type: "Number", description: "เครดิตที่ใช้ไป", example: "5000" },
        { key: "data.account.package.renewalDate", type: "String | null", description: "วันต่ออายุ", example: '"2025-12-31T00:00:00.000Z"' },
        { key: "data.account.apiKeys", type: "Array", description: "รายการ API Keys ที่มี", example: "[{ ... }]" },
        { key: "data.account.accountStatus", type: "Object", description: "ข้อมูลสถานะบัญชี", example: "{ ... }" },
        { key: "data.account.accountStatus.isActive", type: "Boolean", description: "บัญชีใช้งานได้หรือไม่", example: "true" },
        { key: "data.account.accountStatus.isSuspended", type: "Boolean", description: "บัญชีถูกระงับหรือไม่", example: "false" },
        { key: "data.account.accountStatus.isVerified", type: "Boolean", description: "บัญชียืนยันตัวตนแล้วหรือไม่", example: "true" },
        { key: "meta", type: "Object", description: "ข้อมูล Metadata ของ Response", example: "{ ... }" },
        { key: "meta.requestId", type: "String", description: "รหัส Request ID", example: '"req_1717997820509_abc123"' },
        { key: "meta.timestamp", type: "String", description: "เวลา Response (ISO 8601)", example: '"2024-05-29T05:37:00.000Z"' },
        { key: "meta.processingTime", type: "Number", description: "เวลาในการประมวลผล Request (มิลลิวินาที)", example: "8" },
      ],
    },
  ];

  const successExamples = `// ตัวอย่าง Success Response สำหรับ Verify Endpoints (qr-code / image / base64 / url)
// หมายเหตุ: verify endpoints ไม่มี meta และ success/type fields
{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "ver_1717997820509_abc123",
    "decode": "00020101021129370016A000000677010112...",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 100.00,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "นาย สมชาย ทดสอบ",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": {
        "passed": true,
        "reason": "No duplicate found"
      },
      "receiver": {
        "passed": true,
        "matchedConditionIndex": 0
      },
      "amount": {
        "passed": true,
        "expectedAmount": 100,
        "actualAmount": 100
      },
      "date": {
        "passed": true,
        "expectedDate": "2025-10-05T14:48:00.000Z",
        "actualDate": "2025-10-05T05:37:00.000Z"
      }
    }
  }
}

// ตัวอย่าง Success Response สำหรับ Generate PromptPay / Bill Payment
{
  "success": true,
  "code": "200100",
  "type": "QR_GENERATED",
  "message": "QR code generated successfully",
  "data": {
    "qrId": "qr_1717997820509_abc123",
    "qrType": "promptpay",
    "format": "png",
    "qrData": "iVBORw0KGgoAAAANSUhEUg...",
    "payload": "00020101021129370016A000000677010112...",
    "accountInfo": {
      "accountType": "PHONE",
      "accountId": "0612***"
    },
    "amount": 1000.00,
    "timestamp": "2024-05-29T05:37:00.000Z",
    "credits": {
      "used": 1,
      "breakdown": { "generation": 1 }
    }
  },
  "meta": {
    "requestId": "req_1717997820509_abc123",
    "timestamp": "2024-05-29T05:37:00.000Z",
    "processingTime": 50
  }
}

// ตัวอย่าง Success Response สำหรับ History
{
  "success": true,
  "code": "200200",
  "type": "HISTORY_RETRIEVED",
  "message": "History retrieved successfully",
  "data": {
    "history": [
      {
        "id": "ver_1717997820509_abc123",
        "type": "verification",
        "status": "success",
        "amount": 100.00,
        "credits": 1,
        "createdAt": "2025-02-15T10:30:00.000Z"
      }
    ],
    "pagination": {
      "currentPage": 1,
      "totalPages": 5,
      "totalItems": 100,
      "itemsPerPage": 20,
      "hasNextPage": true,
      "hasPreviousPage": false
    },
    "statistics": {
      "totalVerifications": 80,
      "totalGenerations": 20,
      "successRate": "95.00",
      "totalCreditsUsed": 100
    }
  },
  "meta": {
    "requestId": "req_1717997820509_abc123",
    "timestamp": "2024-05-29T05:37:00.000Z",
    "processingTime": 15
  }
}

// ตัวอย่าง Success Response สำหรับ Account Info
{
  "success": true,
  "code": "200300",
  "type": "ACCOUNT_INFO_RETRIEVED",
  "message": "Account information retrieved successfully",
  "data": {
    "account": {
      "customerId": "cust-001",
      "businessName": "บริษัท สลิปเช็ค จำกัด",
      "email": "user@example.com",
      "phone": "0812345678",
      "package": {
        "name": "Pro",
        "tier": "pro",
        "creditsRemaining": 5000,
        "creditsTotal": 10000,
        "creditsUsed": 5000,
        "renewalDate": "2025-12-31T00:00:00.000Z"
      },
      "accountStatus": {
        "isActive": true,
        "isSuspended": false,
        "isVerified": true
      }
    }
  },
  "meta": {
    "requestId": "req_1717997820509_abc123",
    "timestamp": "2024-05-29T05:37:00.000Z",
    "processingTime": 8
  }
}`;

  const errorExamples = `// ตัวอย่าง Error Response (401 - API Key Required)
{
  "success": false,
  "code": "401000",
  "type": "API_KEY_REQUIRED",
  "message": "API key is required",
  "error": "API key is required",
  "meta": {
    "requestId": "550e8400-e29b-41d4-a716-446655440000",
    "timestamp": "2025-01-04T09:42:49.000Z",
    "processingTime": 5
  }
}

// ตัวอย่าง Error Response (401 - Invalid API Key)
{
  "success": false,
  "code": "401001",
  "type": "INVALID_API_KEY",
  "message": "Invalid or expired API key",
  "error": "Invalid or expired API key",
  "meta": {
    "requestId": "550e8400-e29b-41d4-a716-446655440001",
    "timestamp": "2025-01-04T09:42:49.000Z",
    "processingTime": 8
  }
}

// ตัวอย่าง Error Response (422 - Slip Not Found)
{
  "success": false,
  "code": "400000",
  "type": "SLIP_NOT_FOUND",
  "message": "Slip not found or invalid",
  "error": "Slip not found or invalid",
  "meta": {
    "requestId": "550e8400-e29b-41d4-a716-446655440002",
    "timestamp": "2025-01-04T09:42:49.000Z",
    "processingTime": 150
  }
}

// ตัวอย่าง Error Response (402 - Insufficient Credits)
{
  "success": false,
  "code": "402000",
  "type": "INSUFFICIENT_CREDITS",
  "message": "Insufficient credits to process request",
  "error": "Insufficient credits to process request",
  "data": {
    "creditsRequired": 1,
    "creditsRemaining": 0,
    "accountCredit": 0
  },
  "meta": {
    "requestId": "550e8400-e29b-41d4-a716-446655440003",
    "timestamp": "2025-01-04T09:42:49.000Z",
    "processingTime": 12
  }
}

// ตัวอย่าง Error Response (500 - Server Error)
{
  "success": false,
  "code": "500000",
  "type": "INTERNAL_ERROR",
  "message": "An unexpected error occurred",
  "error": "An unexpected error occurred",
  "meta": {
    "requestId": "550e8400-e29b-41d4-a716-446655440004",
    "timestamp": "2025-01-04T09:42:49.000Z",
    "processingTime": 25
  }
}`;

  const commonErrors = [
    // Success Codes (for reference)
    { code: "200000", type: "VERIFY_SUCCESS", message: "Verification completed successfully", httpStatus: 200 },
    { code: "200100", type: "QR_GENERATED", message: "QR code generated successfully", httpStatus: 200 },
    { code: "200101", type: "QR_IMAGE_GENERATED", message: "QR code image generated successfully", httpStatus: 200 },
    { code: "200200", type: "HISTORY_RETRIEVED", message: "History retrieved successfully", httpStatus: 200 },
    { code: "200300", type: "ACCOUNT_INFO_RETRIEVED", message: "Account information retrieved successfully", httpStatus: 200 },
    { code: "200999", type: "OPERATION_SUCCESS", message: "Operation completed successfully", httpStatus: 200 },
    
    // Authentication Errors (401)
    { code: "401000", type: "API_KEY_REQUIRED", message: "API key is required", httpStatus: 401 },
    { code: "401001", type: "INVALID_API_KEY", message: "Invalid or expired API key", httpStatus: 401 },
    { code: "401002", type: "API_KEY_SUSPENDED", message: "API key has been suspended", httpStatus: 401 },
    
    // Payment Required (402)
    { code: "402000", type: "INSUFFICIENT_CREDITS", message: "Insufficient credits to perform operation", httpStatus: 402 },
    
    // Authorization Errors (403)
    { code: "403000", type: "IP_NOT_WHITELISTED", message: "IP address not in whitelist", httpStatus: 403 },
    { code: "403001", type: "ACCOUNT_SUSPENDED", message: "Account has been suspended", httpStatus: 403 },
    { code: "403002", type: "FEATURE_NOT_AVAILABLE", message: "Feature not available in current package", httpStatus: 403 },
    
    // Client Errors - Validation (400)
    { code: "400000", type: "SLIP_NOT_FOUND", message: "Slip not found or verification failed", httpStatus: 400 },
    { code: "400001", type: "VALIDATION_ERROR", message: "Request validation failed", httpStatus: 400 },
    { code: "400002", type: "INVALID_REQUEST_PARAMS", message: "Invalid request parameters", httpStatus: 400 },
    { code: "400003", type: "MISSING_REQUIRED_FIELD", message: "Required field is missing", httpStatus: 400 },
    { code: "400004", type: "INVALID_QR_FORMAT", message: "QR code format is invalid", httpStatus: 400 },
    { code: "400005", type: "INVALID_IMAGE_FORMAT", message: "Image format not supported. Use PNG, JPG, or JPEG", httpStatus: 400 },
    { code: "400006", type: "IMAGE_TOO_LARGE", message: "Image size exceeds maximum limit (10MB)", httpStatus: 400 },
    { code: "400007", type: "INVALID_URL", message: "Invalid image URL provided", httpStatus: 400 },
    { code: "400008", type: "INVALID_BASE64", message: "Invalid base64 image data", httpStatus: 400 },
    { code: "400009", type: "INVALID_AMOUNT", message: "Invalid amount value", httpStatus: 400 },
    { code: "400010", type: "INVALID_DATE_RANGE", message: "Invalid date range specified", httpStatus: 400 },
    
    // Processing Errors (422)
    { code: "422000", type: "QR_DECODE_FAILED", message: "Failed to decode QR code", httpStatus: 422 },
    { code: "422001", type: "OCR_FAILED", message: "Failed to extract text from image", httpStatus: 422 },
    { code: "422002", type: "IMAGE_PROCESSING_FAILED", message: "Failed to process image", httpStatus: 422 },
    { code: "422003", type: "VERIFICATION_FAILED", message: "Verification process failed", httpStatus: 422 },
    { code: "422004", type: "QR_GENERATION_FAILED", message: "Failed to generate QR code", httpStatus: 422 },
    { code: "422005", type: "CHECKSUM_INVALID", message: "QR code checksum validation failed", httpStatus: 422 },
    { code: "422006", type: "AMOUNT_MISMATCH", message: "Amount does not match expected value", httpStatus: 422 },
    { code: "422007", type: "DATE_OUT_OF_RANGE", message: "Transaction date outside specified range", httpStatus: 422 },
    
    // Not Found Errors (404)
    { code: "404000", type: "VERIFICATION_NOT_FOUND", message: "Verification record not found", httpStatus: 404 },
    { code: "404001", type: "QR_NOT_FOUND", message: "QR code not found", httpStatus: 404 },
    { code: "404002", type: "ACCOUNT_NOT_FOUND", message: "Account not found", httpStatus: 404 },
    { code: "404003", type: "ENDPOINT_NOT_FOUND", message: "Endpoint not found", httpStatus: 404 },
    
    // Rate Limit (429)
    { code: "429000", type: "RATE_LIMIT_EXCEEDED", message: "Rate limit exceeded. Please try again later", httpStatus: 429 },
    
    // Server Errors (500)
    { code: "500000", type: "INTERNAL_ERROR", message: "Internal server error occurred", httpStatus: 500 },
    { code: "500001", type: "DATABASE_ERROR", message: "Database operation failed", httpStatus: 500 },
    { code: "500002", type: "EXTERNAL_SERVICE_ERROR", message: "External service unavailable", httpStatus: 500 },
    
    // Service Unavailable (503)
    { code: "503000", type: "SERVICE_MAINTENANCE", message: "Service temporarily unavailable for maintenance", httpStatus: 503 },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Header */}
          <h1 className="text-2xl font-bold text-gray-900">API Response</h1>

          {/* Branch Tabs */}
          <TabGroup defaultValue="response-detail" color="brand-blue" fullWidth>
            <TabGroupList>
              <TabGroupTrigger value="response-detail">รายละเอียด Response</TabGroupTrigger>
              <TabGroupTrigger value="response-success">Success Response</TabGroupTrigger>
              <TabGroupTrigger value="response-error">Error Response</TabGroupTrigger>
            </TabGroupList>

            {/* Response Detail Tab Content */}
            <TabGroupContent value="response-detail">
              {apiEndpoints.map((endpoint, sectionIndex) => (
            <div key={sectionIndex} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
              <div className="space-y-3">
                {endpoint.urls.map((url, urlIndex) => (
                  <div key={urlIndex} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-700 font-medium">Endpoint:</span>
                      <Badge className="bg-brand-primary text-white hover:bg-brand-primary/90">{endpoint.method}</Badge>
                      <span className="text-sm text-brand-primary font-mono">{url}</span>
                    </div>
                    <Badge variant="destructive" className="bg-red-500 hover:bg-red-600">
                      Auth Required
                    </Badge>
                  </div>
                ))}
              </div>

              <h2 className="text-xl font-bold text-gray-900 mb-6">รายละเอียด Response</h2>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold">Key</TableHead>
                      <TableHead className="font-bold">Type</TableHead>
                      <TableHead className="font-bold">Description</TableHead>
                      <TableHead className="font-bold">Example</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {endpoint.fields.map((field, fieldIndex) => (
                      <TableRow key={fieldIndex}>
                        <TableCell className="font-mono text-sm">{field.key}</TableCell>
                        <TableCell className="text-sm">{field.type}</TableCell>
                        <TableCell className="text-sm">{field.description}</TableCell>
                        <TableCell className="font-mono text-sm text-gray-600">{field.example}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
            </TabGroupContent>

            {/* Success Response Tab Content */}
            <TabGroupContent value="response-success">
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                  <h2 className="text-xl font-bold text-gray-900">ตัวอย่าง Success Response สำหรับ API v3</h2>
                  <p className="text-sm text-gray-600">
                    เมื่อ API ทำงานสำเร็จจะคืนค่า <code className="bg-gray-100 px-2 py-1 rounded">code: "200000"</code> พร้อมกับ HTTP status 200
                  </p>

                  <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-6 py-3 border-b border-gray-700">
                      <span className="text-sm font-semibold text-gray-300">Success Response Structure</span>
                      <Badge className="bg-green-500 hover:bg-green-600 text-white">200 OK</Badge>
                    </div>
                    <pre className="p-6 text-sm text-gray-300 overflow-x-auto leading-relaxed">{successExamples}</pre>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">หมายเหตุ:</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
                      <li><code className="bg-blue-100 px-2 py-0.5 rounded">checkCondition</code> จะมีเฉพาะเมื่อส่ง request พร้อมเงื่อนไขการตรวจสอบ</li>
                      <li>ฟิลด์ที่เป็น <code className="bg-blue-100 px-2 py-0.5 rounded">null</code> อาจไม่ปรากฏใน response</li>
                      <li>ทุก endpoint คืนค่าในรูปแบบ <code className="bg-blue-100 px-2 py-0.5 rounded">{`{ code, message, data }`}</code></li>
                    </ul>
                  </div>
                </div>
              </div>
            </TabGroupContent>

            {/* Error Response Tab Content */}
            <TabGroupContent value="response-error">
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                  <h2 className="text-xl font-bold text-gray-900">ตัวอย่าง Error Response สำหรับ API v3</h2>
                  <p className="text-sm text-gray-600">
                    เมื่อเกิดข้อผิดพลาดจะคืนค่า <code className="bg-gray-100 px-2 py-1 rounded">code: "400000"</code> หรือ error code อื่นๆ พร้อมกับ HTTP status code ที่เหมาะสม
                  </p>

                  <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-6 py-3 border-b border-gray-700">
                      <span className="text-sm font-semibold text-gray-300">Error Response Structure</span>
                      <Badge className="bg-red-500 hover:bg-red-600 text-white">4xx/5xx</Badge>
                    </div>
                    <pre className="p-6 text-sm text-gray-300 overflow-x-auto leading-relaxed">{errorExamples}</pre>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                  <h2 className="text-xl font-bold text-gray-900">รายการ Error Code ทั้งหมด</h2>
                  
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-bold">Error Code</TableHead>
                          <TableHead className="font-bold">Error Type</TableHead>
                          <TableHead className="font-bold">HTTP Status</TableHead>
                          <TableHead className="font-bold">คำอธิบาย</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {commonErrors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-mono text-sm font-semibold">{error.code}</TableCell>
                            <TableCell className="font-mono text-sm text-gray-600">{error.type}</TableCell>
                            <TableCell className="text-sm">
                              <Badge 
                                className={
                                  error.httpStatus >= 500
                                    ? "bg-purple-500 hover:bg-purple-600"
                                    : error.httpStatus >= 400
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-green-500 hover:bg-green-600"
                                }
                              >
                                {error.httpStatus}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{error.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h3 className="font-semibold text-orange-900 mb-2">หมายเหตุ:</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-orange-800">
                      <li>Error response จะมี <code className="bg-orange-100 px-2 py-0.5 rounded">success: false</code> เสมอ</li>
                      <li>ทุก error จะมี <code className="bg-orange-100 px-2 py-0.5 rounded">code</code> (ตัวเลข 6 หลัก) และ <code className="bg-orange-100 px-2 py-0.5 rounded">type</code> (ชื่อ error)</li>
                      <li>ค่า <code className="bg-orange-100 px-2 py-0.5 rounded">data</code> อาจจะมีหรือไม่มีขึ้นอยู่กับประเภท error</li>
                      <li>HTTP status code จะสอดคล้องกับประเภทของ error</li>
                      <li>ควรตรวจสอบ <code className="bg-orange-100 px-2 py-0.5 rounded">error.code</code> หรือ <code className="bg-orange-100 px-2 py-0.5 rounded">error.type</code> เพื่อจัดการ error แต่ละประเภท</li>
                      <li>Response จะมี <code className="bg-orange-100 px-2 py-0.5 rounded">meta</code> ที่มี requestId, timestamp และ processingTime</li>
                    </ul>
                  </div>
                </div>
              </div>
            </TabGroupContent>
          </TabGroup>
        </div>
      </div>
    </DashboardLayout>
  )
}
